/**
 * AddToCollectionModal - Add a card instance in three phases: card search → edition → details.
 *
 * Opened with a pre-selected printing (card details panels) it starts in details and has no search phase.
 */
import { usePrevious } from '@uidotdev/usehooks';
import { useEffect, type ReactElement } from 'react';
import { toast } from 'sonner';

import Button from '@/components/Button/Button';
import Modal from '@/components/Modal/Modal';
import { useAddCardMutation } from '@/features/collection/hooks/api';
import type { MinimalCard } from '@/services/cardDatabase';

import useAddCardForm from '../hooks/useAddCardForm';
import useAddToCollectionFlow from '../hooks/useAddToCollectionFlow';
import useSetInfoByCode from '../hooks/useSetInfoByCode';
import AddCardDetailsStep from './AddCardDetailsStep';
import AddCardStepNav from './AddCardStepNav';
import { formDataToCreateCard } from './CardInstanceForm';
import CardSearchStep from './CardSearchStep';
import EditionPickerStep from './EditionPickerStep';

export interface AddToCollectionModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Called when modal should close */
  onClose: () => void;
  /** Pre-selected card (when opened from card details) */
  preSelectedCard?: MinimalCard | null;
  /** Default folder ID (e.g., from set context) */
  defaultFolderId?: string | null;
  /** Set code context (for per-set form memory) */
  setCode?: string | null;
  /** Called after each successful add (including +1 on an existing instance) */
  onSuccess?: () => void;
}

export default function AddToCollectionModal({
  isOpen,
  onClose,
  preSelectedCard,
  defaultFolderId,
  setCode,
  onSuccess,
}: AddToCollectionModalProps): ReactElement {
  const addCardMutation = useAddCardMutation();
  const form = useAddCardForm({ isOpen, defaultFolderId, setCode });
  const flow = useAddToCollectionFlow({ isOpen, preSelectedCard });
  const setInfo = useSetInfoByCode();
  const { phase, selectedCard, selectedPrinting } = flow;

  const prevIsOpen = usePrevious(isOpen);
  useEffect(() => {
    if (isOpen && !prevIsOpen) addCardMutation.reset();
  }, [isOpen, prevIsOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelectCard = (card: MinimalCard) => {
    if (flow.selectCard(card)) form.resetForm();
  };

  const handleSubmit = async (addNext: boolean) => {
    if (!selectedPrinting) return;
    try {
      await addCardMutation.mutateAsync(formDataToCreateCard(selectedPrinting.id, form.formData));
      toast.success(`Added ${form.formData.quantity}× ${selectedPrinting.name} (${selectedPrinting.set.toUpperCase()}) to collection`);
      onSuccess?.();
      if (!addNext) {
        onClose();
        return;
      }
      if (preSelectedCard) form.resetQuantity();
      else form.resetForm();
      flow.resetForNext();
      addCardMutation.reset();
    } catch (error) {
      console.error('Failed to add card to collection:', error);
    }
  };

  const isPending = addCardMutation.isPending;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add to Collection" size="xl">
      <AddCardStepNav
        phase={phase}
        availablePhases={flow.availablePhases}
        canVisit={flow.canVisit}
        onNavigate={flow.goToPhase}
        previousPhase={flow.previousPhase}
        onBack={flow.goBack}
        card={selectedCard}
        printing={selectedPrinting}
        printingSetName={selectedPrinting ? setInfo.get(selectedPrinting.set)?.name : undefined}
      />

      {!preSelectedCard && (
        <CardSearchStep
          key={flow.searchResetKey}
          isActive={phase === 'search'}
          highlightedOracleId={selectedCard?.oracle_id}
          onSelectCard={handleSelectCard}
          className="min-h-100"
        />
      )}

      {phase === 'edition' && (
        <EditionPickerStep printings={flow.printings} selectedId={selectedPrinting?.id} onSelect={flow.selectPrinting} />
      )}

      {phase === 'details' && selectedPrinting && (
        <>
          <AddCardDetailsStep
            key={selectedPrinting.id}
            printing={selectedPrinting}
            formData={form.formData}
            onFormChange={form.handleFormChange}
            onFinishUnavailable={form.setFinishWithoutMemory}
            disabled={isPending}
            memory={{
              rememberedFields: form.rememberedFields,
              onToggle: form.handleToggleRemembered,
              onReset: form.resetRememberedToDefaults,
              contextLabel: form.contextLabel,
            }}
            onIncremented={onSuccess}
          />

          {addCardMutation.isError && (
            <div className="bg-danger/10 text-danger mt-4 rounded-lg px-4 py-3 text-sm" role="alert">
              Failed to add card. Please try again.
            </div>
          )}

          <div className="border-storm mt-6 flex items-center justify-end gap-3 border-t pt-4">
            <Button type="button" text="Cancel" variant="text" onClick={onClose} disabled={isPending} />
            <Button
              type="button"
              text="Add & next"
              variant="outlined"
              color="create"
              onClick={() => handleSubmit(true)}
              disabled={isPending}
              title={preSelectedCard ? 'Add and pick another edition' : 'Add and search for the next card'}
            />
            <Button
              type="button"
              text={isPending ? 'Adding...' : 'Add to Collection'}
              color="create"
              onClick={() => handleSubmit(false)}
              disabled={isPending}
            />
          </div>
        </>
      )}
    </Modal>
  );
}
